import { useCallback, useMemo, useRef, useState } from 'react';

import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { AutoScroller, Cursor, Feedback, PointerSensor, PreventSelection } from '@dnd-kit/dom';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DragIndicator from '@mui/icons-material/DragIndicator';
import { Box, Tooltip } from '@mui/material';

import ClipboardListOutline from '@/assets/icons/ClipboardListOutline';
import Button from '@/components/Buttons/Button';
import type { SymbolKey } from '@/entities/symbol';
import { symbolKey } from '@/entities/symbol';
import AccordionSection from '@/features/DataHub/components/AccordionSection';
import {
  DATAHUB_ITEM_DETAILS_MAX_ROWS,
  DATAHUB_LIST_ROW_HEIGHT,
  DATAHUB_ROW_HEIGHT
} from '@/features/DataHub/constants/ui';
import type { CatalogSymbolItem } from '@/features/DataHub/hooks/useCatalogSymbolItems';
import { useCatalogSymbolItems } from '@/features/DataHub/hooks/useCatalogSymbolItems';
import { useSelectedSymbolsActions } from '@/features/DataHub/hooks/useSelectedSymbolsActions';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';
import { SELECTOR_MODES } from '@/features/SymbolSelector/store/symbolSelector.slice';

const COLUMN_WIDTHS = {
  alias: '80px',
  code: '90px',
  currency: '75px',
  units: '60px'
};

const CURVE_COLUMN_WIDTHS = {
  code: '110px',
  timeRef: '70px',
  periodType: '90px',
  period: '60px'
};

const headerSx = {
  display: 'flex',
  alignItems: 'center',
  p: 2,
  fontSize: '12px',
  gap: '16px'
};
const iconPlaceholderSx = { width: '24px' };
const rowSx = {
  display: 'flex',
  alignItems: 'center',
  p: 1.5,
  height: DATAHUB_ROW_HEIGHT,
  minHeight: DATAHUB_ROW_HEIGHT,
  borderWidth: 1,
  borderColor: 'text.primary',
  borderStyle: 'solid',
  flex: 1,
  fontSize: '14px',
  gap: 2
};
const dragHandleSx = {
  width: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'grab',
  flexShrink: 0
};
const dragIconSx = { fontSize: 24 };
const ellipsisSx = { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const deleteBtnSx = {
  width: '32px',
  height: '32px',
  backgroundColor: 'gx.actionBarButtonTile',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};
const deleteIconSx = { mr: '0 !important' };
const scrollContainerSx = {
  maxHeight: DATAHUB_ITEM_DETAILS_MAX_ROWS * DATAHUB_LIST_ROW_HEIGHT,
  overflow: 'auto'
};
const listSx = { display: 'flex', flexDirection: 'column', gap: 2 };

type CurveItem = {
  id: string;
  code: string;
  timeRef: string;
  periodType: string;
  periodCount: number;
  title: string;
  symbols: SymbolKey[];
};

const useCurveItems = (symbols: SymbolKey[]) => {
  const { items, isLoading } = useCatalogSymbolItems(symbols);

  const curveItems = useMemo(() => {
    const groups = new Map<string, CurveItem>();
    for (const item of items) {
      const key = symbolKey(item.symbol, true);
      const existing = groups.get(key);
      if (existing) {
        existing.periodCount++;
        existing.symbols.push(item.symbol);
      } else {
        groups.set(key, {
          id: key,
          code: item.code,
          timeRef: item.symbol.TimeRef,
          periodType: item.symbol.PeriodType,
          periodCount: 1,
          title: item.title,
          symbols: [item.symbol]
        });
      }
    }
    return Array.from(groups.values());
  }, [items]);

  return { curveItems, isLoading };
};

const CurveItemsHeader = () => (
  <Box sx={headerSx}>
    <Box sx={iconPlaceholderSx} />
    <Box sx={{ width: CURVE_COLUMN_WIDTHS.code }}>GX Code</Box>
    <Box sx={{ width: CURVE_COLUMN_WIDTHS.timeRef }}>TimeRef</Box>
    <Box sx={{ width: CURVE_COLUMN_WIDTHS.periodType }}>PeriodType</Box>
    <Box sx={{ width: CURVE_COLUMN_WIDTHS.period }}>Period</Box>
    <Box sx={{ flex: 1 }}>Short title</Box>
  </Box>
);

const CurveItemRow = ({
  curve,
  index,
  containerElement
}: {
  curve: CurveItem;
  index: number;
  containerElement: HTMLDivElement | null;
}) => {
  const { removeSymbolsFromSelection } = useSelectionFunctions();
  const handleDelete = useCallback(() => {
    removeSymbolsFromSelection(curve.symbols);
  }, [curve.symbols, removeSymbolsFromSelection]);

  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLElement | null>(null);

  useSortable({
    id: curve.id,
    index,
    element: ref,
    handle: handleRef,
    type: 'item',
    accept: 'item',
    modifiers: [RestrictToVerticalAxis, RestrictToElement.configure({ element: containerElement })]
  });

  return (
    <Box
      ref={ref}
      sx={rowSx}
    >
      <Box
        ref={handleRef}
        sx={dragHandleSx}
      >
        <DragIndicator sx={dragIconSx} />
      </Box>
      <Box sx={{ width: CURVE_COLUMN_WIDTHS.code }}>{curve.code}</Box>
      <Box sx={{ width: CURVE_COLUMN_WIDTHS.timeRef }}>{curve.timeRef}</Box>
      <Box sx={{ width: CURVE_COLUMN_WIDTHS.periodType }}>{curve.periodType}</Box>
      <Box sx={{ width: CURVE_COLUMN_WIDTHS.period }}>{curve.periodCount}</Box>
      <Box sx={ellipsisSx}>
        <Tooltip title={curve.title}>
          <Box component='span'>{curve.title}</Box>
        </Tooltip>
      </Box>
      <Button
        aria-label={`Remove curve ${curve.code}`}
        sx={deleteBtnSx}
        onClick={handleDelete}
      >
        <DeleteOutlineIcon sx={deleteIconSx} />
      </Button>
    </Box>
  );
};

const CurveItemDetails = ({ symbols }: { symbols: SymbolKey[] }) => {
  const { curveItems } = useCurveItems(symbols);
  const { reorderSelectedSymbols } = useSelectedSymbolsActions();
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);

  return (
    <AccordionSection
      icon={<ClipboardListOutline />}
      title='Item details'
    >
      <CurveItemsHeader />
      <Box
        ref={setContainerElement}
        sx={scrollContainerSx}
      >
        <DragDropProvider
          plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
          sensors={sensors}
          onDragEnd={event => {
            const reordered = move(curveItems, event);
            reorderSelectedSymbols(reordered.flatMap(c => c.symbols));
          }}
        >
          <Box sx={listSx}>
            {curveItems.map((curve, index) => (
              <CurveItemRow
                key={curve.id}
                containerElement={containerElement}
                curve={curve}
                index={index}
              />
            ))}
          </Box>
        </DragDropProvider>
      </Box>
    </AccordionSection>
  );
};

const sensors = [
  PointerSensor.configure({
    activatorElements(source) {
      return [source.element, source.handle];
    }
  })
];

const ItemsHeader = ({ readOnly }: { readOnly?: boolean }) => {
  return (
    <Box sx={headerSx}>
      {!readOnly && <Box sx={iconPlaceholderSx} />}
      <Box sx={{ width: COLUMN_WIDTHS.alias }}>Alias</Box>
      <Box sx={{ width: COLUMN_WIDTHS.code }}>GX Code</Box>
      <Box sx={{ width: COLUMN_WIDTHS.currency }}>Currency</Box>
      <Box sx={{ width: COLUMN_WIDTHS.units }}>Unit</Box>
      <Box sx={{ flex: 1 }}>Short Title</Box>
    </Box>
  );
};

export const ItemRow = ({
  item,
  readOnly,
  index,
  containerElement
}: {
  item: CatalogSymbolItem;
  readOnly?: boolean;
  index: number;
  containerElement: HTMLDivElement | null;
}) => {
  const { removeSymbolFromSelection } = useSelectionFunctions();
  const handleClick = useCallback(() => {
    removeSymbolFromSelection(item.symbol);
  }, [item.symbol, removeSymbolFromSelection]);

  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLElement | null>(null);

  useSortable({
    id: item.id,
    index,
    element: ref,
    handle: handleRef,
    type: 'item',
    accept: 'item',
    disabled: readOnly,
    modifiers: [RestrictToVerticalAxis, RestrictToElement.configure({ element: containerElement })]
  });

  return (
    <Box
      ref={ref}
      sx={rowSx}
    >
      {!readOnly && (
        <Box
          ref={handleRef}
          sx={dragHandleSx}
        >
          <DragIndicator sx={dragIconSx} />
        </Box>
      )}
      <Box sx={{ width: COLUMN_WIDTHS.alias }}>{item.alias}</Box>
      <Box sx={{ width: COLUMN_WIDTHS.code }}>{item.code}</Box>
      <Box sx={{ width: COLUMN_WIDTHS.currency }}>{item.currency}</Box>
      <Box sx={{ width: COLUMN_WIDTHS.units }}>{item.units}</Box>
      <Box sx={ellipsisSx}>
        <Tooltip title={item.title}>
          <Box component='span'>{item.title}</Box>
        </Tooltip>
      </Box>
      {!readOnly && (
        <Button
          sx={deleteBtnSx}
          onClick={handleClick}
        >
          <DeleteOutlineIcon sx={deleteIconSx} />
        </Button>
      )}
    </Box>
  );
};

export const ItemDetails = ({
  symbols,
  readOnly,
  selectorMode = SELECTOR_MODES.NEUTRAL
}: {
  symbols: SymbolKey[];
  readOnly?: boolean;
  selectorMode?: SELECTOR_MODES;
}) => {
  const { items } = useCatalogSymbolItems(symbols);
  const { reorderSelectedSymbols } = useSelectedSymbolsActions();
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);

  if (selectorMode === SELECTOR_MODES.FORWARD_CURVE) {
    return <CurveItemDetails symbols={symbols} />;
  }

  return (
    <AccordionSection
      icon={<ClipboardListOutline />}
      title='Item details'
    >
      <ItemsHeader readOnly={readOnly} />
      <Box
        ref={setContainerElement}
        sx={scrollContainerSx}
      >
        <DragDropProvider
          plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
          sensors={sensors}
          onDragEnd={event => {
            const reordered = move(items, event);
            const newSymbols = reordered.map(item => item.symbol);
            reorderSelectedSymbols(newSymbols);
          }}
        >
          <Box sx={listSx}>
            {items.map((item, index) => (
              <ItemRow
                key={item.id}
                containerElement={containerElement}
                index={index}
                item={item}
                readOnly={readOnly}
              />
            ))}
          </Box>
        </DragDropProvider>
      </Box>
    </AccordionSection>
  );
};
